import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useStabileListe } from "../components/stabileListe.js";
import type { ApiClient, Rechnung, Unternehmen } from "@kundeninfo/api-client";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { RechnungDetail, useRechnungsUmfeld } from "../components/RechnungDetail.js";
import type { RechnungDetailClient } from "../components/RechnungDetail.js";
import { PDF_HINWEIS_ART, apiMeldung } from "../components/vorgangsdetail.js";
import type { PdfMeldung } from "../components/vorgangsdetail.js";
import "./KundeRechnungen.css";

/**
 * Der RECHNUNGEN-Reiter (Paket 3b-2, Aufgabe 4) — das letzte Glied der Kette
 * Angebot → Auftrag → Rechnung.
 *
 * Vier Dinge unterscheiden ihn von seinen beiden Schwesterreitern, und alle vier folgen
 * daraus, dass eine Rechnung ein BELEG ist und kein Vorgang:
 *
 * **Eine Rechnung entsteht hier nicht.** Es gibt keinen „Neue Rechnung"-Knopf: Jede Rechnung
 * wird am AUFTRAG abgeleitet (Knopf „Rechnung erzeugen" in KundeAuftraege), damit Positionen,
 * Texte und der Zahlungssatz-Snapshot vom Auftrag kommen und nicht von Hand. Der Reiter
 * bekommt die frisch abgeleitete Rechnung über `oeffneRechnungId` gereicht und schlägt sie
 * gleich auf.
 *
 * **Festgeschrieben heisst NUR-LESEND — im DOM, nicht bloss optisch**, und **die frische
 * Version geht IMMER auch in die Liste** (Lehre aus dem Produktfix f746859): Beides ist
 * seit dem Bausteinschnitt (Bereichs-Spalten, Etappe 1, Aufgabe 5b) Sache von
 * `components/RechnungDetail.tsx` — dort steht auch die ausführliche Begründung. Denselben
 * Baustein zeichnet der Rechnungs-BEREICH (views/BereichRechnungen.tsx): Der Reiter behält
 * damit kein eigenes, zweites Detail, das mit dem dritten Fix auseinanderliefe.
 *
 * Beim Reiter bleibt, was zur ANSICHT gehört und nicht zum einzelnen Beleg: die Liste, der
 * Meldungsstreifen, die Auswahlhilfen (Platzhalter, Länder) und die Entscheidung, WELCHE
 * Rechnung gerade aufgeschlagen ist (genau eine) — samt dem Sprung zwischen Original und
 * Korrektur, denn der öffnet eine ANDERE Zeile dieser Liste.
 *
 * Eigener CSS-Block `krg` — mit `kang` (Angebote), `kauf` (Aufträge), `brng`
 * (Rechnungs-Bereich) und `pe__` (Editor) wird KEINE Klasse geteilt (Kollisions-Lehre
 * 01.09.).
 */

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster KundeAngebote/KundeAuftraege). Der grössere
 *  Teil davon gehört seit dem Bausteinschnitt dem Detail (`RechnungDetailClient`); der
 *  Reiter legt nur die LISTE und die Auswahlhilfe dazu. */
export type RechnungenClient = RechnungDetailClient
  & Pick<ApiClient, "listInvoices" | "listPlaceholders">;

/** Die Verdrahtung, die von App über KundenAkte hierher kommt. */
export type RechnungenUmfeld = {
  client: RechnungenClient;
  /** Die Unternehmen, zu denen der Kunde eine Beziehung hat — allein zur Auflösung des
   *  Kürzels an einer bestehenden Rechnung. Angelegt wird hier nichts. */
  companies: Unternehmen[];
  /** Die gerade am Auftrag abgeleitete Rechnung: Der Reiter schlägt sie beim Öffnen auf,
   *  statt den Bearbeiter in einer Liste suchen zu lassen (Muster der Angebot→Auftrag-Naht). */
  oeffneRechnungId?: string | null;
  /** Die Rechnung, die ein Sprung aus der Kundenübersicht aufschlagen soll: wie `oeffneRechnungId`, aber
   *  ohne den Satz „Aus dem Auftrag abgeleitet …" — die Rechnung ist nicht eben erst entstanden. */
  oeffneVorgangId?: string | null;
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ eines Vorgangs in den Reiter „Dateien“ (Akte). */
  onOrdnerOeffnen: (folderId: string) => void;
};

export type KundeRechnungenProps = RechnungenUmfeld & {
  kundeId: string;
  /** Die Sicht der Akte (Mandantentrennung E359) — `undefined` = das aktive Unternehmen. */
  companyIds?: readonly string[] | undefined;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`).
   *  Fehlt er, bleibt das Token in der Vorschau sichtbar stehen (wie an den anderen
   *  Reitern). */
  kundeName?: string;
};

export function KundeRechnungen(p: KundeRechnungenProps): ReactElement {
  const { client, kundeId, companies } = p;

  const [rechnungen, setRechnungen] = useState<Rechnung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Die Laufmeldung des Reiters — MIT ihrer Art (Fund W1 der Prüfung).
   *
   * Vorher stand hier eine blosse Zeichenkette, und gezeichnet wurde sie unbedingt grün und
   * als `role="status"`. In denselben Zustand schreiben aber auch die Fehlschläge und die
   * drei 409-Texte: Ein gescheitertes Festschreiben sah damit aus wie ein gelungenes und
   * wurde dem Screenreader als Status statt als Alarm angesagt. Die Art wird deshalb
   * MITGEFÜHRT und kann an der Aufrufstelle nicht vergessen werden — auch nicht im
   * Baustein, der sie über `onMeldung` hereinreicht.
   */
  const [meldung, setMeldung] = useState<PdfMeldung | null>(null);

  /** Die gerade aufgeschlagene Rechnung — als KENNUNG, nicht als Satz: Der Satz kommt aus
   *  der Liste und wird dort fortgeschrieben; zwei Kopien liefen auseinander. */
  const [geoeffnetId, setGeoeffnetId] = useState<string | null>(null);

  /** Die Auswahlhilfen lädt der REITER, einmal für alle Zeilen — und reicht sie an jedes
   *  Detail durch (Muster `useAngebotsUmfeld`). */
  const umfeld = useRechnungsUmfeld(client);

  /** Der Sprung aus dem Auftrag wird genau EINMAL ausgeführt — sonst risse er den
   *  Bearbeiter nach jedem Neuzeichnen zurück in den Entwurf. */
  const sprungErledigt = useRef(false);

  const sicht = useStabileListe(p.companyIds);
  const laden = useCallback(async (): Promise<Rechnung[]> => {
    try {
      const { items } = await client.listInvoices(kundeId, sicht);
      setRechnungen(items);
      setFehler(null);
      return items;
    } catch (e) {
      setFehler(apiMeldung(e, "Die Rechnungen konnten nicht geladen werden."));
      setRechnungen([]);
      return [];
    }
  }, [client, kundeId, sicht]);

  useEffect(() => { void laden(); }, [laden]);

  // Spec Mandantentrennung R21: Ein Chipwechsel der Akte klappt die aufgeschlagene Rechnung
  // zu — sie muss in der neuen Sicht nicht stehen, und ihre Kennung sperrte sonst jede andere
  // Zeile. Beim ersten Zeichnen ist ohnehin nichts aufgeschlagen.
  useEffect(() => {
    setGeoeffnetId(null);
    setMeldung(null);
  }, [sicht]);

  /** Eine Rechnung aufschlagen — mit einem Satz dazu, was gerade passiert ist. Alles, was
   *  zum Beleg selbst gehört (Entwurf, Positionen, Ablagestand), setzt der Baustein selbst
   *  zurück, sobald er aufschlägt. */
  const oeffnen = useCallback((id: string, hinweisText: string | null = null): void => {
    setMeldung(hinweisText === null ? null : { text: hinweisText, art: "gut" });
    setGeoeffnetId(id);
  }, []);

  function schliessen(): void {
    setGeoeffnetId(null);
  }

  // Der Sprung aus dem Auftrag: Die dort abgeleitete Rechnung wird aufgeschlagen, sobald die
  // Liste steht — mit einem Satz dazu, was gerade passiert ist. Ohne ihn sähe der Bearbeiter
  // nach dem Ableiten nur eine Listenzeile und müsste raten, ob etwas übernommen wurde.
  // Der Sprung aus der Kundenübersicht (`oeffneVorgangId`) schlägt ebenso auf, aber ohne den Satz.
  useEffect(() => {
    const id = p.oeffneRechnungId ?? p.oeffneVorgangId;
    if (id === undefined || id === null || rechnungen === null) return;
    if (sprungErledigt.current) return;
    if (!rechnungen.some((r) => r.id === id)) return;
    sprungErledigt.current = true;
    oeffnen(id, id === p.oeffneRechnungId
      ? "Aus dem Auftrag abgeleitet — Positionen, Texte und der Zahlungssatz sind übernommen. "
        + "Die Rechnung ist noch ein Entwurf."
      : null);
  }, [p.oeffneRechnungId, p.oeffneVorgangId, rechnungen, oeffnen]);

  /**
   * Der frische Stand EINER Rechnung in der Liste. Die eine Stelle, durch die jede
   * Serverantwort eines Schreibwegs läuft: Die Knöpfe rechnen mit `r.version`, und wer sie
   * hier nicht nachzieht, bekommt beim nächsten Klick eine 409 auf seine eigene Änderung.
   */
  const uebernehmen = useCallback((frisch: Rechnung): void => {
    setRechnungen((alle) => (alle === null
      ? alle : alle.map((r) => (r.id === frisch.id ? frisch : r))));
  }, []);

  /**
   * Der SPRUNG zwischen Original und Korrektur — er schlägt die andere Rechnung im selben
   * Reiter auf. Die Rückfrage bei ungespeichertem Kopf hat der Baustein schon gestellt
   * (`darfVerlassenWerden`); hier geht es nur noch darum, den Satz zu finden.
   *
   * Gesucht wird zuerst in der geladenen Liste; erst wenn sie ihn nicht führt (ein anderer
   * Bearbeiter hat die Korrektur eben erst angelegt), wird neu geladen. Ein unbedingtes
   * Neuladen bei jedem Klick wäre eine Wartezeit für den Regelfall, in dem beide Rechnungen
   * ohnehin schon dastehen.
   */
  async function springeZu(id: string): Promise<void> {
    if ((rechnungen ?? []).some((r) => r.id === id)) {
      oeffnen(id);
      return;
    }
    const frisch = await laden();
    if (!frisch.some((r) => r.id === id)) {
      setMeldung({
        text: "Diese Rechnung wurde nicht gefunden — der Stand ist neu geladen.",
        art: "schlecht",
      });
      return;
    }
    oeffnen(id);
  }

  /**
   * Die frisch angelegte (oder schon bestehende) KORREKTURRECHNUNG aufnehmen und
   * aufschlagen.
   *
   * Neu geladen wird die GANZE Liste, nicht nur der neue Satz: Es haben sich zwei Sätze
   * geändert — die Korrektur ist dazugekommen, und das Original trägt nun den Rückverweis,
   * an dem sein Knopf hängt. Führt die frische Liste den neuen Beleg wider Erwarten nicht
   * (eine Trefferseite ist immer ein Ausschnitt), wird der mitgereiste Satz angehängt: Nach
   * einem gelungenen Klick vor „nicht gefunden" zu stehen wäre die schlechteste aller
   * Auskünfte.
   */
  async function korrekturAufnehmen(korrektur: Rechnung, hinweisText: string): Promise<void> {
    const frisch = await laden();
    if (!frisch.some((r) => r.id === korrektur.id)) {
      setRechnungen((alle) => [...(alle ?? []), korrektur]);
    }
    oeffnen(korrektur.id, hinweisText);
  }

  // ISO-Zeitstempel sortieren lexikografisch = chronologisch → neueste zuerst.
  const sortiert = [...(rechnungen ?? [])].sort(
    (a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <div className="krg">
      <div className="krg__kopf">
        <h2 className="krg__titel">Rechnungen</h2>
      </div>

      {/* Es gibt hier bewusst keinen „Neue Rechnung"-Knopf — siehe Kopfkommentar. */}
      <p className="krg__hinweis">
        Rechnungen entstehen am Auftrag („Rechnung erzeugen"), damit Positionen, Texte und
        Zahlungsbedingung von dort kommen.
      </p>

      {/* Als `Hinweis` (ME-Etappe 3, Aufgabe 5): Die Art der Meldung entscheidet über Farbe
          UND Rolle (`PDF_HINWEIS_ART`). Schlechtestes oben: der Ladefehler vor der
          Laufmeldung. */}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {meldung !== null && (
        <Hinweis art={PDF_HINWEIS_ART[meldung.art]}>{meldung.text}</Hinweis>
      )}

      {rechnungen === null && fehler === null && <p className="krg__hinweis">Lädt …</p>}

      {rechnungen !== null && sortiert.length === 0 && (
        <p className="krg__leer">Für diesen Kunden ist noch keine Rechnung erfasst.</p>
      )}

      {sortiert.length > 0 && (
        <ul className="krg__liste">
          {sortiert.map((r) => (
            <li key={r.id} className="krg__eintrag">
              <RechnungDetail
                client={client} kundeId={kundeId} rechnung={r} companies={companies}
                umfeld={umfeld} offen={geoeffnetId === r.id}
                gesperrt={geoeffnetId !== null}
                {...(p.kundeName === undefined ? {} : { kundeName: p.kundeName })}
                onOeffnen={() => oeffnen(r.id)}
                onSchliessen={schliessen}
                onNeuLaden={laden}
                onMeldung={setMeldung}
                onFortschreiben={uebernehmen}
                onSpringeZuRechnung={(id) => { void springeZu(id); }}
                onKorrekturAngelegt={korrekturAufnehmen}
                onOrdnerOeffnen={p.onOrdnerOeffnen} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
